import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";

export function HaltPage(): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ["halt"],
    queryFn: api.halt,
  });

  if (isLoading) return <main className="text-chrome-300">Loading…</main>;
  if (error || !data) return <main className="text-crit">Failed to load.</main>;

  return (
    <main>
      <PageHeader
        eyebrow="emergency stop"
        title="Halt"
        subtitle="Stop the current scan immediately. Halt is irreversible within a scan window — a new scan must be scheduled to resume."
      />

      <section className="panel rule-crit relative overflow-hidden p-8">
        <div className="flex items-center gap-3">
          <span
            className={[
              "pulse-dot",
              data.halted ? "text-crit" : "text-chrome-400",
            ].join(" ")}
          />
          <span
            className={[
              "text-xs font-medium uppercase tracking-widest",
              data.halted ? "text-crit-400 text-glow-crit" : "text-chrome-300",
            ].join(" ")}
          >
            {data.halted ? "halted · scan stopped" : "scans active · halt available"}
          </span>
        </div>

        <p className="mt-4 max-w-2xl text-sm text-chrome-300">{data.explanation}</p>

        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          <div className="rounded border border-ink-400 bg-ink-600 p-5">
            <div className="label-overline">halt url</div>
            <a
              href={data.halt_url}
              className="code mt-2 block break-all text-sm text-cyber-400 underline-offset-2 hover:underline"
            >
              {data.halt_url}
            </a>
          </div>
          <div className="rounded border border-ink-400 bg-ink-600 p-5">
            <div className="label-overline">halt code</div>
            <div className="code text-cyber-400 text-glow-cyber mt-2 text-2xl font-semibold tracking-[0.2em]">
              {data.halt_code}
            </div>
            <div className="mt-2 text-[11px] text-chrome-400">
              entered locally on the shim host
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center gap-3">
          <button
            type="button"
            disabled={data.halted}
            className={[
              "rounded border px-5 py-2.5 text-xs font-medium uppercase tracking-widest transition",
              data.halted
                ? "cursor-not-allowed border-ink-400 bg-ink-600 text-chrome-500"
                : "border-crit/50 bg-crit/15 text-crit-400 shadow-glow-crit hover:bg-crit/25",
            ].join(" ")}
          >
            {data.halted ? "✓ already halted" : "✕ halt now"}
          </button>
          {data.last_halt_at && (
            <span className="text-[11px] text-chrome-400">
              last halt {new Date(data.last_halt_at).toLocaleString()}
            </span>
          )}
        </div>
      </section>
    </main>
  );
}
